import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, User, Lock, ArrowRight, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { adminLogin } from "../../lib/adminApi";

import { useAppConfig } from "../../lib/AppConfigContext";

export default function AdminLoginPage() {
  const { t } = useTranslation();
  const { appName } = useAppConfig();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lockedInfo, setLockedInfo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await adminLogin(username, password);
      localStorage.setItem("admin_username", response.data.email || response.data.username || username);
      localStorage.setItem("admin_name", response.data.name || "Admin");
      toast.success(t('admin.sessionStarted'));
      navigate("/admin/dashboard");
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      if (error.response?.status === 423 && detail.startsWith("account_locked")) {
        const minutes = detail.split("|")[1] || "15";
        setLockedInfo(minutes);
      } else {
        toast.error(detail || t('admin.credentialsError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <div className="text-center">
            <h1 className="font-heading font-bold text-2xl text-white">{appName}</h1>
            <p className="text-sm text-slate-400">{t('admin.panelAdmin')}</p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 shadow-xl">
          <h2 className="text-xl font-heading font-bold text-white mb-2 text-center">
            {t('admin.accessAdmin')}
          </h2>
          <p className="text-slate-400 mb-6 text-center text-sm">
            {t('admin.enterAdminCredentials')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {lockedInfo && (
              <div className="bg-red-900/40 border border-red-700/50 rounded-lg p-4" data-testid="admin-locked-banner">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-300 mb-1">{t('admin.adminAccountLockedTitle')}</p>
                    <p className="text-sm text-red-400 mb-3">{t('admin.adminAccountLocked', { minutes: lockedInfo })}</p>
                    <p className="text-sm text-red-400">{t('admin.adminAccountLockedSuggest')}</p>
                  </div>
                </div>
              </div>
            )}
            {!lockedInfo && (
            <>
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-300">
                {t('admin.username')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 h-11 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                  placeholder="admin"
                  required
                  data-testid="admin-username"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                  placeholder="••••••••"
                  required
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white"
              disabled={loading}
              data-testid="admin-login-submit"
            >
              {loading ? (
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {t('admin.access')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            </>
            )}
          </form>
        </div>

        <p className="text-center mt-6 text-slate-500 text-sm">
          <a href="/login" className="text-amber-400 hover:text-amber-300">
            ← {t('admin.backToEntityLogin')}
          </a>
        </p>
      </motion.div>
    </div>
  );
}
